import { AppDispatch, RootState } from '../Store';
import { Api } from '../../Api/Api';
import { createTracedAsyncThunk } from '../../../tracing/trace';
import { soyId } from './CropsSlice';
import { getCurrentActingUser } from '../User/AuthSlice';
import { ICreateCustomTreatmentRequest, ICustomTreatmentResponse } from '../../Models/Responses/CustomTreatmentResponse';

export interface ISyngentaReferenceTreatment {
	/**
	 * The Primary Key for the Treatment table.
	 */
	Id: string;

	/**
	 * A 3-character Code that is included in the material and is unique in the year.
	 */
	Code: string;

	/**
	 * A 3-character Abbreviation that is descriptive and unique in the year.
	 */
	Abbreviation: string;

	/**
	 * A Sales-Text name for the treatment. This is NOT unique, and should probably NOT be used unless in marketing
	 * material.
	 */
	Name: string;

	/**
	 * What the user would see as the verbose name of the treatment. UI labeling should use this field.
	 */
	Description: string;

	/**
	 * The crop that this treatment is associated with.
	 * Foreign key to the Crop table.
	 */
	CropId: string;

	/**
	 * The year that this treatment is valid for. This is used to determine if the treatment is valid for a given
	 * hybrid set.  Note that not every combination of hybrid and treatment will be sold.
	 */
	Year: number;

	IsUntreated: boolean;
}

/**
 * Get Syngenta Reference Treatments for Soybeans.
 * 
 * This will return all Syngenta treatments for the current crop year.
 */
export const getSyngentaTreatments = createTracedAsyncThunk<ISyngentaReferenceTreatment[], void, { dispatch: AppDispatch; state: RootState; }>(
	'get/treatments/syngenta',
	async (context, request, thunkAPI) => 
	{
		const currentState = thunkAPI.getState();
		try 
		{
			const api = new Api('/api/7', currentState.auth.userAuthToken, context);
			const cropId = soyId;
			const year = currentState.crops.CropYear;
			const url = `/crops/${cropId}/years/${year}/treatments/syngenta`;
			const response = await api.getAsync<ISyngentaReferenceTreatment[]>(url);

			if (response.ErrorCode === null && response.Success) 
			{
				return response.Data;
			}

			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e) 
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

/**
 * Get Custom Treatments for Soybeans.
 * 
 * This will return all custom treatments for the current user and specifically soy
 */
export const getCustomTreatments = createTracedAsyncThunk<ICustomTreatmentResponse[], void, { dispatch: AppDispatch; state: RootState; }>(
	'get/treatments/custom',
	async (context, request, thunkAPI) => 
	{
		const currentState = thunkAPI.getState();
		try 
		{
			const api = new Api('/api/7', currentState.auth.userAuthToken, context);
			const userIdentifier = getCurrentActingUser(currentState).UserId;
			const cropId = soyId;
			const url = `/users/${userIdentifier}/crops/${cropId}/treatments/custom`;
			const response = await api.getAsync<ICustomTreatmentResponse[]>(url);

			if (response.ErrorCode === null && response.Success) 
			{
				return response.Data;
			}

			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e) 
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

/**
 * Get a User's selected default treatment for Soybeans, if any.
 * 
 * This will return the treatment ID that the user has selected as their default treatment for soybeans.
 */
export const getUserDefaultTreatment = createTracedAsyncThunk<string | undefined, void, { dispatch: AppDispatch; state: RootState; }>(
	'get/treatments/default',
	async (context, request, thunkAPI) => 
	{
		const currentState = thunkAPI.getState();
		try 
		{
			const api = new Api('/api/7', currentState.auth.userAuthToken, context);
			const userIdentifier = getCurrentActingUser(currentState).UserId;
			const cropId = soyId;
			const url = `/users/${userIdentifier}/crops/${cropId}/treatments/default`;
			const response = await api.getAsync<string | undefined>(url);

			if (response.ErrorCode === null && response.Success) 
			{
				return response.Data;
			}

			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e) 
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

/**
 * Create a new custom treatment for Soybeans.
 */
export const createCustomTreatment = createTracedAsyncThunk<ICustomTreatmentResponse, ICreateCustomTreatmentRequest, { dispatch: AppDispatch; state: RootState; }>(
	'create/treatment/custom',
	async (context, request, thunkAPI) => 
	{
		const currentState = thunkAPI.getState();
		try 
		{
			const api = new Api('/api/7', currentState.auth.userAuthToken, context);
			const userIdentifier = getCurrentActingUser(currentState).UserId;
			const cropId = soyId;
			const url = `/users/${userIdentifier}/crops/${cropId}/treatments/custom`;
			const response = await api.postAsync<ICustomTreatmentResponse>(url, {
				Name: request.Name,
				Notes: request.Notes,
				PricePerBag: request.PricePerBag
			});

			if (response.ErrorCode === null && response.Success) 
			{
				return response.Data;
			}

			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e) 
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

/**
 * Update an existing custom treatment for Soybeans
 */
export const updateCustomTreatment = createTracedAsyncThunk<ICustomTreatmentResponse, ICustomTreatmentResponse, { dispatch: AppDispatch; state: RootState; }>(
	'update/treatment/custom',
	async (context, request, thunkAPI) => 
	{
		const currentState = thunkAPI.getState();
		try 
		{
			const api = new Api('/api/7', currentState.auth.userAuthToken, context);
			const url = `/treatments/custom/${request.Id}`;
			const response = await api.putAsync<ICustomTreatmentResponse>(url, request);

			if (response.ErrorCode === null && response.Success) 
			{
				return response.Data;
			}

			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e) 
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

/**
 * Delete a custom treatment for Soybeans.
 */
export const deleteCustomTreatment = createTracedAsyncThunk<ICustomTreatmentResponse, ICustomTreatmentResponse, { dispatch: AppDispatch; state: RootState; }>(
	'delete/treatment/custom',
	async (context, request, thunkAPI) => 
	{
		const currentState = thunkAPI.getState();
		try 
		{
			const api = new Api('/api/7', currentState.auth.userAuthToken, context);
			const url = `/treatments/custom/${request.Id}`;
			const response = await api.deleteAsync<ICustomTreatmentResponse>(url);

			if (response.ErrorCode === null && response.Success) 
			{
				return response.Data;
			}

			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e) 
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

/**
 * Select a default treatment by Id for a user for Soybeans.
 */
export const setUserDefaultTreatment = createTracedAsyncThunk<string, string, { dispatch: AppDispatch; state: RootState; }>(
	'set/treatment/default',
	async (context, request, thunkAPI) => 
	{
		const currentState = thunkAPI.getState();
		try 
		{
			const api = new Api('/api/7', currentState.auth.userAuthToken, context);
			const userIdentifier = getCurrentActingUser(currentState).UserId;
			const cropId = soyId;
			const url = `/users/${userIdentifier}/crops/${cropId}/treatments/default`;
			const response = await api.putAsync<string>(url, request);

			if (response.ErrorCode === null && response.Success) 
			{
				return response.Data;
			}

			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e) 
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);
